import { useState, useId, type ReactNode, type KeyboardEvent } from "react"

export interface Props {
  heading: string
  children: ReactNode
  defaultOpen?: boolean
}

const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5"
    aria-hidden="true"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const MinusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-5 h-5"
    aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const AccordionItem = ({ heading, children, defaultOpen = false }: Props) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const id = useId()
  const triggerId = `accordion-trigger-${id}`
  const panelId = `accordion-panel-${id}`

  const toggle = () => setIsOpen(prev => !prev)

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      toggle()
    }
  }

  return (
    <div className="rounded-2xl overflow-hidden bg-white shadow-sm">
      <div
        id={triggerId}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        className="flex items-center justify-between gap-4 p-6 cursor-pointer select-none"
        style={{ color: "#1E3A5F" }}
      >
        <span className="min-w-0 flex-1 text-xl font-semibold leading-tight text-camp-primary break-words">
          {heading}
        </span>
        <span className="flex-shrink-0">
          {isOpen ? <MinusIcon /> : <PlusIcon />}
        </span>
      </div>

      <div
        id={panelId}
        role="region"
        aria-labelledby={triggerId}
        className={`grid overflow-hidden transition-[grid-template-rows] duration-200 ease-in-out ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={`px-6 pb-6 transition-opacity duration-200 ease-in-out ${
              isOpen ? "opacity-100" : "opacity-0"
            }`}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

AccordionItem.displayName = "AccordionItem"

export { AccordionItem }
export default AccordionItem
